import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute/ProtectedRoute';
import Layout from './components/layout/Layout/Layout';
import Splash from './pages/Splash/Splash';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';

// Article Pages
import ArticleList from './pages/Articles/ArticleList';
import ViewArticle from './pages/Articles/ViewArticle';
import CreateArticle from './pages/Articles/CreateArticle';
import EditArticle from './pages/Articles/EditArticle';

// Team Pages
import TeamList from './pages/Team/TeamList';
import ViewTeam from './pages/Team/ViewTeam';
import CreateTeam from './pages/Team/CreateTeam';
import EditTeam from './pages/Team/EditTeam';

import styles from './App.module.css';

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className={styles.app}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Splash />} />
              <Route path="/login" element={<Login />} />

              {/* Protected Routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >

                <Route path="dashboard" element={<Dashboard />} />


                <Route path="top-news" element={<ArticleList />} />
                <Route path="articles" element={<ArticleList />} />
                <Route path="articles/view/:id" element={<ViewArticle />} />

                {/* Create Article - Editors and above */}
                <Route
                  path="articles/create"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'editor', 'developer', 'blogger']}>
                      <CreateArticle />
                    </ProtectedRoute>
                  }
                />

                {/* Edit Article - Editors and above */}
                <Route
                  path="articles/edit/:id"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'editor', 'developer', 'blogger']}>
                      <EditArticle />
                    </ProtectedRoute>
                  }
                />

                {/* ==========================================
                    TEAM MANAGEMENT
                    ========================================== */}
                {/* Team List - Admin and Developer can view */}
                <Route
                  path="team"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'developer']}>
                      <TeamList />
                    </ProtectedRoute>
                  }
                />

                {/* View Team Member - Admin and Developer can view */}
                <Route
                  path="team/view/:id"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'developer']}>
                      <ViewTeam />
                    </ProtectedRoute>
                  }
                />

                {/* Create Team Member - SuperAdmin only */}
                <Route
                  path="team/create"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin']}>
                      <CreateTeam />
                    </ProtectedRoute>
                  }
                />

                {/* Edit Team Member - SuperAdmin and Admin */}
                <Route
                  path="team/edit/:id"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
                      <EditTeam />
                    </ProtectedRoute>
                  }
                />

                {/* ==========================================
                    OTHER ROUTES (Coming Soon)
                    ========================================== */}
                <Route
                  path="webhooks"
                  element={
                    <ProtectedRoute allowedRoles={['superadmin', 'developer']}>
                      <div className={styles.placeholder}>
                        <h1>⚡ Webhooks</h1>
                        <p className={styles.comingSoon}>Coming Soon...</p>
                      </div>
                    </ProtectedRoute>
                  }
                />

                
                

                <Route
                  path="profile"
                  element={
                    <div className={styles.placeholder}>
                      <h1>👤 Profile</h1>
                      <p className={styles.comingSoon}>Coming Soon...</p>
                    </div>
                  }
                />

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-md)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: 'var(--shadow-lg)',
                },
                success: {
                  iconTheme: {
                    primary: 'var(--success)',
                    secondary: 'white',
                  },
                },
                error: {
                  iconTheme: {
                    primary: 'var(--error)',
                    secondary: 'white',
                  },
                },
                loading: {
                  iconTheme: {
                    primary: 'var(--primary-blue)',
                    secondary: 'white',
                  },
                },
              }}
            />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;